import React, { useEffect, useRef, useState } from 'react';
import { Film } from 'lucide-react';
import { getHelpVideoPublicUrl } from '@/config/environment-config';
import { cn } from '@/lib/utils';
import type { HelpEntry } from './help-types';
import { HelpRichText } from './HelpRichText';
import {
  CONTROLS_ZONE,
  captionAtTop,
  captionFontPx,
  captionMaxChars,
  captionPageAt,
  captionPages,
  splitCaptionLines,
} from './help-captions';

export interface HelpVideoPlayerProps {
  entry: Pick<HelpEntry, 'id' | 'title' | 'video' | 'captions' | 'steps'> & Partial<Pick<HelpEntry, 'poster'>>;
  /** Start playing at once: the viewer chose this video from a tile. */
  autoPlay?: boolean;
  /** Called when the video plays to its end. */
  onEnded?: () => void;
}

interface ActiveCue {
  text: string;
  start: number;
  end: number;
  top: boolean;
}

function readActiveCue(track: TextTrack): ActiveCue | null {
  const cue = track.activeCues?.[0] as VTTCue | undefined;
  if (!cue) return null;
  return { text: cue.text, start: cue.startTime, end: cue.endTime, top: cue.line === 0 };
}

/**
 * Plays a self-hosted help video (Firebase Storage, ADR-0025) with its captions on. The
 * videos are silent, so the captions carry the voiceover. The player draws them itself:
 * at most two short lines in small letters on a dark backing at the bottom, moved to the top while the viewer's
 * pointer is over the controls or while the action in the video is at the bottom (cue
 * `line:0`). In fullscreen (including the iPhone's native player) the browser's own
 * captions take over. Nothing plays until the
 * viewer presses play or picks the video from a tile (`autoPlay`). Without a published video it shows "Video coming soon" with the
 * script's Interim steps.
 */
export function HelpVideoPlayer({ entry, autoPlay = false, onEnded }: HelpVideoPlayerProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cue, setCue] = useState<ActiveCue | null>(null);
  const [time, setTime] = useState(0);
  const [width, setWidth] = useState(640);
  const [pointerLow, setPointerLow] = useState(false);
  const [paused, setPaused] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const captionTrack = () =>
      Array.from(video.textTracks ?? []).find((track) => track.kind === 'captions' || track.kind === 'subtitles');
    // iPhone fullscreen is the native player: it never sets document.fullscreenElement.
    let nativeFullscreen = false;
    // Hidden: the cues still fire, but the browser does not draw them (we do), except in fullscreen.
    const applyMode = () => {
      const track = captionTrack();
      if (track) track.mode = document.fullscreenElement === video || nativeFullscreen ? 'showing' : 'hidden';
    };
    const onNativeFullscreenStart = () => {
      nativeFullscreen = true;
      applyMode();
    };
    const onNativeFullscreenEnd = () => {
      nativeFullscreen = false;
      applyMode();
    };
    const onCueChange = () => {
      const track = captionTrack();
      setCue(track ? readActiveCue(track) : null);
    };
    const onTime = () => setTime(video.currentTime);
    const onPlayState = () => setPaused(video.paused);
    const watchTrack = () => {
      applyMode();
      captionTrack()?.addEventListener('cuechange', onCueChange);
    };
    watchTrack();
    video.textTracks?.addEventListener?.('addtrack', watchTrack);
    video.addEventListener('loadedmetadata', watchTrack);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlayState);
    video.addEventListener('pause', onPlayState);
    document.addEventListener('fullscreenchange', applyMode);
    video.addEventListener('webkitbeginfullscreen', onNativeFullscreenStart);
    video.addEventListener('webkitendfullscreen', onNativeFullscreenEnd);
    return () => {
      captionTrack()?.removeEventListener('cuechange', onCueChange);
      video.textTracks?.removeEventListener?.('addtrack', watchTrack);
      video.removeEventListener('loadedmetadata', watchTrack);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlayState);
      video.removeEventListener('pause', onPlayState);
      document.removeEventListener('fullscreenchange', applyMode);
      video.removeEventListener('webkitbeginfullscreen', onNativeFullscreenStart);
      video.removeEventListener('webkitendfullscreen', onNativeFullscreenEnd);
    };
  }, [entry.video, entry.captions]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([item]) => setWidth(item.contentRect.width));
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [entry.video]);

  if (!entry.video) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4" data-testid={`help-placeholder-${entry.id}`}>
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Film className="h-4 w-4" aria-hidden />
          Video coming soon
        </p>
        <ol className="list-decimal space-y-1.5 pl-5 text-sm text-foreground">
          {entry.steps.map((step) => (
            <li key={step}>
              <HelpRichText text={step} />
            </li>
          ))}
        </ol>
      </div>
    );
  }

  const fontPx = captionFontPx(width);
  const lines = cue
    ? captionPageAt(captionPages(splitCaptionLines(cue.text, captionMaxChars(width, fontPx))), cue.start, cue.end, time)
    : [];
  const atTop = captionAtTop(cue?.top ?? false, pointerLow);

  return (
    <div
      ref={wrapperRef}
      className="relative overflow-hidden rounded-lg border border-border bg-black"
      onPointerMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setPointerLow(event.clientY - rect.top > rect.height * (1 - CONTROLS_ZONE));
      }}
      onPointerLeave={() => setPointerLow(false)}
    >
      <video
        ref={videoRef}
        key={entry.video}
        // Fullscreen uses the browser's captions: the same small type on a dark backing.
        className="block aspect-video w-full [&::cue]:bg-black/80 [&::cue]:text-[0.8em] [&::cue]:[text-shadow:0_1px_2px_rgba(0,0,0,0.9)]"
        controls
        playsInline
        preload="metadata"
        autoPlay={autoPlay}
        onEnded={onEnded}
        crossOrigin="anonymous"
        src={getHelpVideoPublicUrl(entry.video)}
        poster={entry.poster ? getHelpVideoPublicUrl(entry.poster) : undefined}
        aria-label={entry.title}
        data-testid={`help-video-${entry.id}`}
      >
        {entry.captions && (
          <track kind="captions" srcLang="en" label="English" src={getHelpVideoPublicUrl(entry.captions)} default />
        )}
      </video>
      {lines.length > 0 && (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 flex justify-center px-3 transition-[top,bottom] duration-200',
            // While paused the browser shows its control bar, so a bottom caption sits above it.
            atTop ? 'top-2' : paused ? 'bottom-16' : 'bottom-2',
          )}
          data-testid={`help-caption-${entry.id}`}
          data-position={atTop ? 'top' : 'bottom'}
        >
          {/* At most two lines on a dark backing with a soft shadow, distinct on any frame. */}
          <span
            className="max-w-[90%] rounded-md bg-black/80 px-2.5 py-1 text-center font-medium leading-snug text-white shadow-[0_2px_10px_rgba(0,0,0,0.6)] [text-shadow:0_1px_2px_rgba(0,0,0,0.9)]"
            style={{ fontSize: `${fontPx}px` }}
          >
            {lines.map((text, index) => (
              <span key={index} className="block">
                {text}
              </span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
}
